import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Search, ExternalLink, ShoppingBag, ArrowRight, Sparkles } from "lucide-react";

const CATS = ["All", "T-Shirts", "Hoodies", "Caps", "Mugs", "Posters", "Tote Bags", "Stickers"];
const DEFAULT_SHOP = "https://www.hohvibesshop.com/";

function MerchCard({ product, shopUrl }) {
  const href = product.external_url || shopUrl;
  const fallbackImg = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='400'><rect width='100%' height='100%' fill='%23141414'/><text x='50%' y='50%' fill='%23D4AF37' font-family='serif' font-size='42' text-anchor='middle' dominant-baseline='middle'>HOH</text></svg>";
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      data-testid={`merch-card-${product.id}`}
      className="luxury-card group block overflow-hidden rounded-xl"
    >
      <div className="relative aspect-square overflow-hidden bg-black">
        <img
          src={product.cover_image_url || fallbackImg}
          alt={product.title}
          onError={(e) => { e.currentTarget.src = fallbackImg; }}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
        <div className="absolute top-3 left-3 px-2 py-1 bg-black/60 backdrop-blur-md border border-white/10 rounded text-[10px] tracking-widest uppercase text-[#D4AF37]">
          {product.category}
        </div>
        {product.stock_status === "low_stock" && (
          <div className="absolute top-3 right-3 px-2 py-1 bg-amber-900/80 border border-amber-500/30 rounded text-[10px] tracking-widest uppercase text-amber-200">Low Stock</div>
        )}
        {product.stock_status === "out_of_stock" && (
          <div className="absolute top-3 right-3 px-2 py-1 bg-red-900/80 border border-red-500/30 rounded text-[10px] tracking-widest uppercase text-red-200">Sold Out</div>
        )}
      </div>
      <div className="p-5 space-y-3">
        <div>
          <h3 className="font-serif-display text-xl text-white group-hover:text-[#D4AF37] transition-colors leading-tight">{product.title}</h3>
          <p className="text-sm text-neutral-400 mt-1 line-clamp-2">{product.description}</p>
        </div>
        <div className="flex items-center justify-between pt-2">
          <div>
            <div className="text-[10px] tracking-widest text-neutral-500 uppercase">
              {product.sizes?.length ? `${product.sizes.length} sizes` : "Single option"}
            </div>
            <div className="font-serif-display text-2xl text-[#D4AF37]">£{Number(product.price).toFixed(2)}</div>
          </div>
          <span
            data-testid={`shop-merch-${product.id}`}
            className="btn-gold py-2 px-4 rounded inline-flex items-center gap-2 text-xs tracking-widest uppercase"
          >
            <ShoppingBag size={14} /> Shop <ExternalLink size={12} />
          </span>
        </div>
      </div>
    </a>
  );
}

export default function MerchStore() {
  const [products, setProducts] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [category, setCategory] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [shopUrl, setShopUrl] = useState(DEFAULT_SHOP);
  const [shopName, setShopName] = useState("HOH_Vibes Shop");

  useEffect(() => {
    api.get("/storefront").then((r) => {
      if (r.data?.merch_shop_url) setShopUrl(r.data.merch_shop_url);
      if (r.data?.merch_shop_name) setShopName(r.data.merch_shop_name);
    }).catch(() => {});
    api.get("/products", { params: { type: "merch", featured: true } })
      .then((r) => setFeatured(r.data.slice(0, 4)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { type: "merch" };
    if (category !== "All") params.category = category;
    if (q) params.q = q;
    api.get("/products", { params }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [category, q]);

  return (
    <div data-testid="merch-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5" data-testid="merch-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1581655353564-df123a1eb820?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-[#0A0A0A]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="max-w-3xl">
            <div className="label-eyebrow flex items-center gap-2 mb-6">
              <Sparkles size={14} /> Merch · External Shop
            </div>
            <h1 className="font-serif-display text-5xl lg:text-6xl text-white leading-[1.05]">
              Wear the <span className="italic text-[#D4AF37]">vibes</span>.
            </h1>
            <p className="text-neutral-300 mt-6 max-w-2xl text-lg leading-relaxed">
              Premium gospel-inspired apparel and accessories. All merchandise orders are fulfilled by our partner store <span className="text-[#D4AF37]">{shopName}</span> — secure checkout, worldwide shipping.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href={shopUrl}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="merch-hero-shop-btn"
                className="btn-gold px-7 py-3 rounded inline-flex items-center gap-2 text-sm tracking-widest uppercase font-semibold"
              >
                <ShoppingBag size={16} /> Shop Merchandise <ExternalLink size={14} />
              </a>
              <a
                href="#merch-gallery"
                className="btn-ghost-gold px-7 py-3 rounded inline-flex items-center gap-2 text-sm tracking-widest uppercase font-semibold"
              >
                Browse Gallery <ArrowRight size={14} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="merch-featured">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="label-eyebrow mb-3">01 · Featured Drops</div>
              <h2 className="font-serif-display text-4xl lg:text-5xl text-white">This season's <span className="italic text-[#D4AF37]">favorites</span></h2>
            </div>
            <a
              href={shopUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="hidden sm:inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-[#D4AF37]"
              data-testid="merch-featured-view-all"
            >
              View all on shop <ExternalLink size={14} />
            </a>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((p) => <MerchCard key={p.id} product={p} shopUrl={shopUrl} />)}
          </div>
        </section>
      )}

      {/* GALLERY */}
      <section id="merch-gallery" className="max-w-7xl mx-auto px-6 lg:px-10 py-20 border-t border-white/5" data-testid="merch-gallery-section">
        <div className="label-eyebrow mb-3">02 · The Gallery</div>
        <h2 className="font-serif-display text-4xl lg:text-5xl text-white">Every <span className="italic text-[#D4AF37]">piece</span>, one mission.</h2>

        <div className="mt-10 flex flex-col lg:flex-row gap-6 lg:items-center justify-between">
          <div className="relative max-w-md w-full">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              data-testid="merch-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search merch..."
              className="w-full bg-black border border-white/15 focus:border-[#D4AF37] outline-none pl-11 pr-4 py-3 rounded text-white text-sm"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {CATS.map((c) => (
              <button
                key={c}
                data-testid={`mcategory-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setCategory(c)}
                className={`px-3 py-1.5 text-xs tracking-widest uppercase rounded border transition-colors ${
                  category === c ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-neutral-300 hover:border-[#D4AF37]/50"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="merch-grid">
          {loading ? (
            <div className="col-span-full text-neutral-500 text-sm">Loading...</div>
          ) : products.length === 0 ? (
            <div className="col-span-full text-neutral-500">No merch found.</div>
          ) : (
            products.map((p) => <MerchCard key={p.id} product={p} shopUrl={shopUrl} />)
          )}
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="border-t border-white/5 bg-gradient-to-b from-transparent to-[#0F172A]/40" data-testid="merch-cta-banner">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-24 text-center">
          <div className="label-eyebrow mb-3 justify-center">03 · The Full Catalog</div>
          <h2 className="font-serif-display text-4xl lg:text-5xl text-white">
            Ready to <span className="italic text-[#D4AF37]">wear the vibes?</span>
          </h2>
          <p className="text-neutral-300 mt-6 max-w-2xl mx-auto leading-relaxed">
            Browse the full HOH_Vibes merchandise catalog, choose sizes &amp; colors, and check out securely on our partner Shopify store.
          </p>
          <a
            href={shopUrl}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="merch-cta-shop-btn"
            className="btn-gold mt-10 inline-flex items-center gap-2 px-8 py-4 rounded text-sm tracking-widest uppercase font-semibold"
          >
            <ShoppingBag size={16} /> Visit {shopName} <ExternalLink size={14} />
          </a>
        </div>
      </section>
    </div>
  );
}
